import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { of } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BulkCountResponse,
  BulkReviewAction,
  ProductAdsBulkPreviewItem,
  ProductAdsBulkReviewRow,
  BulkReviewRowAction,
  BulkReviewRowStatus,
  PreviewRowStatus,
} from './product-ads-bulk-review-row.interface';
import { TreelistColumnDef } from 'src/app/theme/shared/forecastui/forecast-rxtreelist/treelist-column-def.interface';
import { TreelistLevelConfig } from 'src/app/theme/shared/forecastui/forecast-rxtreelist/treelist-level-config.interface';
import {
  ManagedScope,
} from 'src/app/core/models/product-ads';
import { BulkExecutePayload, ItemAdsPair, PreviewBulkPayload, ProductAdsService } from 'src/app/core/services/product-ads.service';
import { TreeSelection } from 'src/app/theme/shared/forecastui/forecast-rxtreelist/forecast-rxtreelist.component';
import { ProductAdsActionService } from '../../services/product-ads-action.service';
import _ from 'lodash';

const ACTION_META_BASE: Record<BulkReviewAction, { icon: string; color: string; label: string; actionText: { true: string; false: string }; colorOverride?: { true: string; false: string } }> = {
  active: { 
    icon: 'fas fa-sync', 
    color: '#22c55e', 
    label: 'Active Status', 
    actionText: { true: 'Enabled', false: 'Paused' },
    colorOverride: { true: '#22c55e', false: '#f44336' } // Red for Pause
  },
  isAutomatic: { 
    icon: 'fas fa-robot', 
    color: '#4099ff', 
    label: 'Auto Mode', 
    actionText: { true: 'Auto', false: 'Manual' }
  },
  managedBySystem: { 
    icon: 'fas fa-tasks', 
    color: '#FFB64D', 
    label: 'Managed By System', 
    actionText: { true: 'Managed', false: 'Unmanaged' }
  },
};

const STATE_LABEL: Record<BulkReviewAction, { true: string; false: string }> = {
  active: { true: 'Enabled', false: 'Paused' },
  isAutomatic: { true: 'Auto', false: 'Manual' },
  managedBySystem: { true: 'Managed', false: 'Unmanaged' },
};

const STATUS_BADGE_LABEL: Record<
  BulkReviewAction,
  { [BulkReviewRowStatus.WILL_UPDATE]: PreviewRowStatus; [BulkReviewRowStatus.ALREADY_CURRENT]: (targetValue: boolean) => PreviewRowStatus }
> = {
  active: {
    [BulkReviewRowStatus.WILL_UPDATE]: PreviewRowStatus.WILL_CHANGE,
    [BulkReviewRowStatus.ALREADY_CURRENT]: (t) => PreviewRowStatus.NO_CHANGE,
  },
  isAutomatic: {
    [BulkReviewRowStatus.WILL_UPDATE]: PreviewRowStatus.WILL_CHANGE,
    [BulkReviewRowStatus.ALREADY_CURRENT]: (t) => PreviewRowStatus.NO_CHANGE,
  },
  managedBySystem: {
    [BulkReviewRowStatus.WILL_UPDATE]: PreviewRowStatus.WILL_CHANGE,
    [BulkReviewRowStatus.ALREADY_CURRENT]: (t) => PreviewRowStatus.NO_CHANGE,
  },
};

@Component({
  selector: 'app-product-ads-bulk-review-modal',
  templateUrl: './product-ads-bulk-review-modal.component.html',
  styleUrls: ['./product-ads-bulk-review-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAdsBulkReviewModalComponent implements OnInit, OnDestroy {
  @Input() action!: BulkReviewAction;
  @Input() targetValue!: boolean;
  @Input() isSelectAll!: boolean;
  @Input() selectedKeys?: TreeSelection[];
  @Input() filter?: any;
  @Input() excludeIds?: TreeSelection[];
  @Input() managedScope?: ManagedScope;
  @Input() totalRootCount: number = 0;
  @Input() startDate?: Date;
  @Input() endDate?: Date;
  @Input() treeNodeMap?: Map<string, any>;

  rootItems: ProductAdsBulkReviewRow[] = [];
  isLoading = false;
  isCountLoading = false;
  hasLoaded = false;
  isConfirmed = false;
  bulkCounts: BulkCountResponse | null = null;
  filteredTotal = 0;
  searchTerm = '';
  private searchSubject = new Subject<string>();

  readonly levelConfigs: TreelistLevelConfig[] = [
    {
      name: 'campaigns',
      fetch: (id: string) =>
        of(this.rootItems.find((r) => r.id === id)?.campaigns ?? []),
      hasChildren: (row: ProductAdsBulkReviewRow) => (row.campaigns?.length ?? 0) > 0,
    },
  ];

  private readonly STATUS_WILL_UPDATE: BulkReviewRowStatus = BulkReviewRowStatus.WILL_UPDATE;
  private readonly STATUS_ALREADY_CURRENT: BulkReviewRowStatus = BulkReviewRowStatus.ALREADY_CURRENT;
  private readonly matchWillUpdate = (r: ProductAdsBulkReviewRow) => r.status === this.STATUS_WILL_UPDATE;
  private readonly matchAlreadyCurrent = (r: ProductAdsBulkReviewRow) => r.status === this.STATUS_ALREADY_CURRENT;

  private destroy$ = new Subject<void>();

  readonly columns: TreelistColumnDef[] = [
    {
      field: 'label',
      title: 'SKU / CAMPAIGN',
      cellType: 'text',
      expandable: true,
      visible: true,
      sortable: false,
      filterable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'currentState',
      title: 'CURRENT',
      cellType: 'text',
      visible: true,
      sortable: false,
      filterable: false,
      hiddenInColumnChooser: true,
      width: 120,
    },
    {
      field: 'rowAction',
      title: '',
      cellType: 'text',
      visible: true,
      sortable: false,
      filterable: false,
      hiddenInColumnChooser: true,
      width: 60,
    },
    {
      field: 'newState',
      title: 'NEW',
      cellType: 'text',
      visible: true,
      sortable: false,
      filterable: false,
      hiddenInColumnChooser: true,
      width: 120,
    },
    {
      field: 'status',
      title: 'CHANGE',
      cellType: 'text',
      visible: true,
      sortable: false,
      filterable: false,
      hiddenInColumnChooser: true,
      width: 200,
    },
  ];

  get actionMeta() {
    const meta = ACTION_META_BASE[this.action];
    const color = meta.colorOverride ? meta.colorOverride[String(this.targetValue)] : meta.color;
    const actionText = meta.actionText[String(this.targetValue)];
    return { ...meta, color, actionText };
  }

  get actionLabel(): string {
    return this.actionMeta.label;
  }

  get totalItemsWillUpdate(): number {
    return (this.bulkCounts?.skuWillUpdate ?? 0) + (this.bulkCounts?.adWillUpdate ?? 0);
  }

  get totalItemsAlreadyCurrent(): number {
    return (this.bulkCounts?.skuAlreadyCurrent ?? 0) + (this.bulkCounts?.adAlreadyCurrent ?? 0);
  }

  get totalItemsCount(): number {
    return this.totalItemsWillUpdate + this.totalItemsAlreadyCurrent;
  }

  get totalSkuCount(): number {
    return (this.bulkCounts?.skuWillUpdate ?? 0) + (this.bulkCounts?.skuAlreadyCurrent ?? 0);
  }

  get totalAdCount(): number {
    return (this.bulkCounts?.adWillUpdate ?? 0) + (this.bulkCounts?.adAlreadyCurrent ?? 0);
  }

  get totalSkuWillUpdate(): number {
    return this.bulkCounts?.skuWillUpdate ?? this.countParents(this.matchWillUpdate);
  }

  get totalSkuAlreadyCurrent(): number {
    return this.bulkCounts?.skuAlreadyCurrent ?? this.countParents(this.matchAlreadyCurrent);
  }

  get totalAdWillUpdate(): number {
    return this.bulkCounts?.adWillUpdate ?? this.countChildren(this.matchWillUpdate);
  }

  get totalAdAlreadyCurrent(): number {
    return this.bulkCounts?.adAlreadyCurrent ?? this.countChildren(this.matchAlreadyCurrent);
  }

  get bucketBarData() {
    const willUpdate = this.totalAdWillUpdate;
    const total = this.totalAdCount || 1;
    const willUpdatePercent = (willUpdate / total) * 100;

    return {
      willUpdate,
      alreadyCurrent: this.totalAdAlreadyCurrent,
      willUpdatePercent,
      alreadyCurrentPercent: 100 - willUpdatePercent,
      targetState: this.actionMeta.actionText,
      currentState: this.action === 'active' ? (this.targetValue ? 'Paused' : 'Enabled') : 
                    this.action === 'isAutomatic' ? (this.targetValue ? 'Manual' : 'Auto') :
                    (this.targetValue ? 'Unmanaged' : 'Managed')
    };
  }

  public pageSize = 20;
  public skip = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly productAdsService: ProductAdsService,
    private readonly actionService: ProductAdsActionService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public onPageChange(event: { skip: number; take: number }): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.loadPreview();
  }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(term => {
      this.searchTerm = term;
      this.skip = 0;
      this.loadPreview();
    });

    this.loadPreview();
    this.loadBulkCounts();
  }

  onSearch(event: any): void {
    this.searchSubject.next(event.target.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // For preview payloads (new format with level-separated arrays)
  private toItemAdsPairs(selection: TreeSelection[]): ItemAdsPair[] {
    const pairs: ItemAdsPair[] = [];

    const groupAdIdByItemKey = _.groupBy(selection.filter(k => k.parentId), 'parentId') as Record<string, TreeSelection[]>;
    
    for (const [key, ads] of Object.entries(groupAdIdByItemKey)) {
      pairs.push({itemKey: key, adIds: ads.map(ad => String(ad.id))});
    }

    const onlyItemPairs = (selection.filter(k => !k.parentId && !groupAdIdByItemKey[String(k.id)]) ?? []).map(k => ({itemKey: String(k.id)}));

    return [...pairs, ...onlyItemPairs];
  }

  private loadPreview(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    const isQuery = this.isSelectAll;

    const payload: PreviewBulkPayload = {
      ...(isQuery ? {
        mode: 'query',
        offset: this.skip,
        limit: this.pageSize,
        excludes: this.toItemAdsPairs(this.excludeIds ?? [])
      } : {
        mode: 'explicit',
        includes: this.toItemAdsPairs(this.selectedKeys ?? [])
      }),
      action: this.action,
      targetValue: this.targetValue,
      managedScope: this.managedScope ?? ManagedScope.MANAGED,
      where: this.filter,
      startDate: this.startDate?.toISOString(),
      endDate: this.endDate?.toISOString(),
      changeFilter: 'WILL_UPDATE',
      searchTerm: this.searchTerm || undefined,
    };

    this.productAdsService
      .previewBulk(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ items, total }) => {
          this.hasLoaded = true;
          this.rootItems = items.map((item) => this.mapPreviewItem(item));
          this.filteredTotal = total;
        },
        error: () => {
          // Handled by finalize
        },
      });
  }

  private loadBulkCounts(): void {
    this.isCountLoading = true;
    this.cdr.markForCheck();
    const isQuery = this.isSelectAll;

    this.productAdsService
      .previewBulkCount({
        ...(isQuery ? {
          mode: 'query',
          excludes: this.toItemAdsPairs(this.excludeIds ?? [])
        } : {
          mode: 'explicit',
          includes: this.toItemAdsPairs(this.selectedKeys ?? [])
        }),
        action: this.action,
        targetValue: this.targetValue,
        managedScope: this.managedScope ?? ManagedScope.MANAGED,
        where: this.filter,
        startDate: this.startDate?.toISOString(),
        endDate: this.endDate?.toISOString(),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isCountLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (counts) => {
          this.bulkCounts = counts;
        },
        error: () => {
          // Handled by finalize
        },
      });
  }

  private mapPreviewItem(item: ProductAdsBulkPreviewItem): ProductAdsBulkReviewRow {
    const campaigns = item.campaigns.map((campaign) => ({
      id: campaign.adId,
      label: campaign.campaignName,
      currentState: this.getStateLabel(this.action, campaign.currentValue),
      newState: this.getStateLabel(this.action, this.targetValue),
      rowAction: this.getRowAction(campaign.currentValue),
      status: this.getStatus(campaign.currentValue, this.targetValue),
    }));

    // assign default values for parent for safe fallback
    let finalParentWillChange = item.currentValue !== this.targetValue;
    let parentTargetValue = this.targetValue;

    /*
      First we will get the tree node of this item key
      so we can use all the children of this tree node
      to check if all the campaign are targeted
    */

    if (this.treeNodeMap && this.treeNodeMap.has(item.itemKey)) {
      const parentNode = this.treeNodeMap.get(item.itemKey);
      const allChildren = parentNode.children || [];
      const previewChildMap = new Map(item.campaigns.map(c => [c.adId, this.targetValue]));
      /*
       * We must evaluate all children from the tree, not just `item.campaigns`, 
       * because we also need the current state of any unselected children.
       * Parent becomes `true` if ANY child evaluates to `true`.
       * If this new state differs from the current state -> "Will Change", else "No Change".
       */
      parentTargetValue = allChildren.some((child: any) => {
        const futureValue = previewChildMap.has(child.adId) 
          ? previewChildMap.get(child.adId) 
          : child[this.action];

        return futureValue === true;
      });

      finalParentWillChange = item.currentValue !== parentTargetValue;
    } 

    return {
      id: item.itemKey,
      label: item.sku,
      currentState: this.getStateLabel(this.action, item.currentValue),
      /*
        For the newState
        If finalParentWillChange is true -> it means that the parent will change state
        If finalParentWillChange is false -> it means that the parent will not change state
      */
      newState: this.getStateLabel(this.action, finalParentWillChange ? false : true ),
      rowAction: finalParentWillChange ? BulkReviewRowAction.UPDATE : BulkReviewRowAction.SKIP,
      status: finalParentWillChange ? this.STATUS_WILL_UPDATE : this.STATUS_ALREADY_CURRENT,
      campaigns,
    };
  }

  private countParents(pred: (r: ProductAdsBulkReviewRow) => boolean): number {
    return this.rootItems.filter(pred).length;
  }

  private countChildren(pred: (r: ProductAdsBulkReviewRow) => boolean): number {
    return this.rootItems.reduce((sum, p) => sum + (p.campaigns?.filter(pred).length ?? 0), 0);
  }

  private getStateLabel(action: BulkReviewAction, value: boolean): string {
    return STATE_LABEL[action][value ? 'true' : 'false'];
  }

  private getStatus(currentValue: boolean, newValue: boolean = this.targetValue): BulkReviewRowStatus {
    return currentValue === newValue ? this.STATUS_ALREADY_CURRENT : this.STATUS_WILL_UPDATE;
  }

  private getRowAction(currentValue: boolean): BulkReviewRowAction {
    return currentValue === this.targetValue ? BulkReviewRowAction.SKIP : BulkReviewRowAction.UPDATE;
  }

  getStatusLabel(status: BulkReviewRowStatus): PreviewRowStatus | string {
    const config = STATUS_BADGE_LABEL[this.action];
    if (status === this.STATUS_WILL_UPDATE) return config[BulkReviewRowStatus.WILL_UPDATE];
    return config[BulkReviewRowStatus.ALREADY_CURRENT](this.targetValue);
  }

  getActionDisplay(rowAction: BulkReviewRowAction): string {
    return rowAction === BulkReviewRowAction.UPDATE ? '→' : '−';
  }

  confirm(): void {
    if (this.action === 'active') {
      this.isConfirmed = true;
      this.isLoading = true;
      this.cdr.markForCheck();
      
      const isQuery = this.isSelectAll;

      const executePayload: BulkExecutePayload = {
        ...(isQuery ? {
          mode: 'query',
          excludes: this.toItemAdsPairs(this.excludeIds ?? [])
        } : {
          mode: 'explicit',
          includes: this.toItemAdsPairs(this.selectedKeys ?? [])
        }),
        action: this.action,
        targetValue: this.targetValue,
        managedScope: this.managedScope ?? ManagedScope.MANAGED,
        where: this.filter,
        startDate: this.startDate?.toISOString(),
        endDate: this.endDate?.toISOString(),
      };

      const action$ = this.actionService.bulkToggleActiveStatusAsync(executePayload);

      action$.pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      ).subscribe({
        next: () => {
          this.activeModal.close(true);
        },
        error: () => {
          this.isConfirmed = false;
        }
      });
    } else {
      this.activeModal.close(true);
    }
  }
}
